<template>
  <div v-if="!currentUser">
    <h2>User not found.</h2>
    <p>The user profile could not be found.</p>
    <p>
      The user may have been disabled or removed, or the link may be incorrect.
    </p>
  </div>
  <div v-else>
    <tabs v-if="tabs && tabs.length > 0 && isCurrentUser">
      <tabs-item
        v-for="(tab, key) in tabs"
        :key="key"
        :active="key === activeTab"
        @click="setActiveTab(tab)"
        >{{ tab.title }}</tabs-item
      >
    </tabs>
    <div class="row p-user-profile">
      <section class="col-xs-12 col-md-8">
        <profile-header :currentUser="isCurrentUser" @editBio="onEditBio">
          <template slot="userAvatar">
            <user-avatar :user="currentUser" />
          </template>
          <template slot="userInfo">
            <article class="p-user-profile__info">
              <h1>{{ currentUser.name }}</h1>
              <p>
                {{ currentUser.position }}
                <br v-if="currentUser.department" />
                {{ currentUser.department }}
                <br v-if="currentUser.location" />
                {{ currentUser.location }}
              </p>

              <p>
                <a :href="`mailto:${currentUser.email}`">
                  <icon name="mail" :fill="false" />
                  {{ " " }}{{ currentUser.email }}
                </a>
                <br />
                <a :href="`tel:${currentUser.phone}`" v-if="currentUser.phone">
                  <icon name="phone" :fill="false" />
                  {{ " " }}{{ currentUser.phone }}
                </a>
              </p>
            </article>
          </template>
          <template slot="userBio">
            <p v-if="isCurrentUser && !currentUser.bio">
              Your bio is not set up yet. Click the edit button to create one
              now
            </p>
            <p v-else-if="!isCurrentUser && !currentUser.bio">
              {{ currentUser.name }} has still not written a profile.
            </p>

            <div class="p-user-profile__bio" v-else v-html="currentUser.bio" />
          </template>
        </profile-header>

        <section-block class="c-group-block">
          <template slot="header"
            >Interest Groups</template
          >
          <template slot="actions">
            <router-link :to="{ name: 'GroupsList' }" class="td-none"
              >Manage Interests</router-link
            >
          </template>
          <p v-if="!isCurrentUser && !currentUser.interestgroups">
            {{ currentUser.name }} has not joined any interest groups
          </p>

          <p v-else-if="isCurrentUser && !currentUser.interestgroups">
            You have not joined any interest groups yet.
            <router-link :to="{ name: 'GroupsList' }"
              >View interest groups here</router-link
            >
          </p>
          <table class="data-table" v-else>
            <tbody>
              <tr v-for="group in currentUser.interestgroups" :key="group.id">
                <td>{{ group.name }} ({{ group.user_count }} members)</td>
              </tr>
            </tbody>
          </table>
        </section-block>

        <section-block class="c-stats-block" headerHidden v-if="gamification">
          <ol class="listless">
            <li>
              <div>
                <strong>Last Active</strong>
                <date :date="date" :relative="true" />
              </div>
            </li>
            <li>
              <div>
                <p>2</p>
                <h3>Ranking</h3>
              </div>
            </li>
            <li>
              <div>
                <p>2</p>
                <h3>Total Points</h3>
              </div>
            </li>
            <li>
              <div>
                <p>2</p>
                <h3>Questions Asked</h3>
              </div>
            </li>
            <li>
              <div>
                <p>2</p>
                <h3>Ideas Submitted</h3>
              </div>
            </li>
          </ol>
        </section-block>

        <section-block class="c-stats-block">
          <template slot="header"
            >Specialisms</template
          >
          <template slot="actions">
            <router-link
              :to="{
                name: 'ProfileEditSection',
                params: { section: 'specialisms' },
              }"
              class="td-none"
              >Edit</router-link
            >
          </template>
          <p v-if="isCurrentUser && !currentUser.specialism">
            You have not added any specialisms yet. Click the edit button to
            create one now
          </p>
          <p v-else-if="!isCurrentUser && !currentUser.bio">
            {{ currentUser.name }} has still not added any specialisms.
          </p>

          <div
            class="p-user-profile__bio"
            v-else
            v-html="currentUser.specialism"
          />
        </section-block>
      </section>

      <section class="u-hide-mobile col-md-4">
        <base-container
          title="Latest News"
          viewLink="/news"
          :withGradient="articles.length > 1"
        >
          <base-scroll-list>
            <ol v-if="articles.length > 0">
              <li v-for="(article, index) in articles" :key="index">
                <card-list
                  v-bind="article"
                  @click="handleNewsClick(article)"
                ></card-list>
              </li>
            </ol>
            <p v-else>There are no articles</p>
          </base-scroll-list>
        </base-container>
      </section>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import filesApi from "@/api/files";
import {
  BaseContainer,
  BaseCardList as CardList,
  BaseScrollableList as BaseScrollList,
} from "@/components/Containers";

import { Date } from "@/components/elements";
const TextArea = () => import("@/components/forms/TextArea");
// import TextArea from "@/components/forms/TextArea";
const ProfileHeader = () => import("@/components/ProfileHeader");
const UserAvatar = () => import("@/components/UserAvatar");
import UserProfile from "@/mixins/UserProfile";
export default {
  name: "UserProfile",

  mixins: [UserProfile],

  components: {
    BaseContainer,
    CardList,
    BaseScrollList,
    ProfileHeader,
    Date,
    TextArea,
    UserAvatar,
  },

  data() {
    return {
      loading: true,
      userAvatar: null,
      isCurrentUser: false,
      date: "2019-01-01T00:00:00",
      editBio: false,
      gamification: false,
    };
  },

  computed: {
    ...mapState("News", ["articles"]),
    ...mapState("users", ["currentUser"]),
  },

  methods: {
    setActiveTab(tab) {
      this.$router.push({ path: tab.to });
    },
    onEditBio() {
      this.editBio = !this.editBio;
    },
    updateBio() {
      const payload = (({ id, bio }) => ({ id, bio }))(this.currentUser);
      this.$store
        .dispatch("users/updateBio", payload)
        .then((resp) => {
          this.editBio = false;
        })
        .catch((err) => {
          console.log("bio error", err);
        });
    },
    handleNewsClick(article) {
      this.$router.push({
        name: "NewsRead",
        params: { id: article.id },
      });
    },
  },

  created() {
    // if we are looking at someones specific profile, get them, else load current user
    this.$store.commit("News/RESET_FILTERS");
    this.$store.commit("News/SET_RESULT_COUNT", 5);
    this.$store
      .dispatch("News/list")
      .then(() => {})
      .catch(() => console.log("news fail"));

    if (this.$route.params.id) {
      this.$store
        .dispatch("users/read", {
          userid: this.$route.params.id,
          setCurrent: true,
        })
        .then((resp) => {
          this.loading = false;
          this.isCurrentUser = false;
        })
        .catch((err) => {
          // alert('error');
        });
    } else {
      this.$store.dispatch("users/getCurrentUser").then((resp) => {
        this.loading = false;
        this.isCurrentUser = true;
      });
    }
  },
};
</script>

<style lang="scss" scoped>
.p-user-profile {
  &__bio {
    white-space: pre-wrap;
  }
}
.c-stats-block {
  ol {
    display: flex;
    flex-direction: column;

    @media screen and (min-width: 1280px) {
      flex-wrap: wrap;
      flex-direction: row;
    }

    li {
      flex-basis: 100%;
      flex: 1;
      text-align: center;
      padding-bottom: 20px;

      margin: 10px 0 20px;

      border-bottom: 1px solid $colour-nhs-pale-grey;

      @media screen and (min-width: 1280px) {
        margin: 0;
        border-bottom: none;
        border-right: 1px solid $colour-nhs-pale-grey;
        padding: 0 10px;
        height: 100px;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      &:nth-of-type(1) {
        p {
          font-size: 1rem;
        }

        div {
          display: inline-flex;
          flex-direction: column;
          margin: 0 auto;
        }
        @media screen and (min-width: 1280px) {
          flex: 2;
        }
      }

      &.no-stretch {
        flex: auto;
        &:nth-of-type(1) {
          @media screen and (min-width: 1280px) {
            flex: auto;
          }
        }
      }

      &:last-of-type {
        border: 0;
      }

      p {
        font-size: 2.75rem;
        font-weight: bold;
        margin: 0;
        padding: 0;
        line-height: 1;
      }

      h3 {
        margin: 0;
      }
    }
  }
}

.c-group-block {
  li {
    text-align: center;
    padding-bottom: 20px;

    margin: 10px 0 20px;

    border-bottom: 1px solid $colour-nhs-pale-grey;
    display: block;

    @media screen and (min-width: 1280px) {
      margin: 0;
      border-bottom: none;
      border-right: 1px solid $colour-nhs-pale-grey;
      padding: 0 10px;
      span {
        max-width: 200px;
        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
        display: inline-block;
      }
      display: inline-block;
      align-items: center;
      justify-content: center;
    }

    &:last-of-type {
      border: 0;
    }

    p {
      font-size: 2.75rem;
      font-weight: bold;
      margin: 0;
      padding: 0;
      line-height: 1;
    }

    h3 {
      margin: 0;
    }
  }
}

.data-table {
  width: 100%;
  thead {
    th {
      text-align: left;
      padding: 10px;
      font-weight: bold;
    }
    border-bottom: 1px solid $colour-nhs-pale-grey;
  }

  tbody {
    td {
      padding: 10px;
      border-bottom: 1px solid $colour-nhs-pale-grey;
    }
  }
}
</style>
